import { useEffect, useRef, useCallback, useState } from "react";

const STORAGE_KEY = "task-tracker-notifications";
const CRITICAL_STORAGE_KEY = "task-tracker-notifications-critical";

function getStoredPref(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

function setStoredPref(enabled: boolean) {
  try {
    localStorage.setItem(STORAGE_KEY, String(enabled));
  } catch {
    // localStorage not available
  }
}

const STATUS_LABELS: Record<string, string> = {
  complete: "completed",
  deferred: "deferred",
  waiting: "waiting on dependencies",
  in_progress: "started",
  failed: "failed",
  setup: "in setup",
  needs_human: "needs human review",
  skipped: "skipped",
  pending: "pending",
};

interface TaskStatus {
  taskId: string;
  status: string;
  subject?: string;
}

interface UseTaskNotificationsOpts {
  statuses?: TaskStatus[];
  enabled?: boolean;
  projectName?: string;
}

/**
 * useTaskNotifications — browser notifications on task status changes (PRD Section 8)
 *
 * Reads task statuses (from context via useRawTaskStatuses or passed directly).
 * Fires browser Notifications on status changes. Batches multiple changes.
 * Persists enabled state to localStorage key: task-tracker-notifications.
 */
export function useTaskNotifications(opts: UseTaskNotificationsOpts = {}) {
  const { statuses, projectName } = opts;

  // If `enabled` is explicitly passed, use it as initial; otherwise read from localStorage
  const [enabledState, setEnabledState] = useState(() => {
    if (opts.enabled !== undefined) return opts.enabled;
    return getStoredPref();
  });

  // Track prop-driven enabled if provided
  useEffect(() => {
    if (opts.enabled !== undefined) {
      setEnabledState(opts.enabled);
    }
  }, [opts.enabled]);

  const enabled = enabledState;

  const prevStatusMap = useRef<Map<string, string> | null>(null);
  const initialized = useRef(false);
  const batchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingChangesRef = useRef<
    { taskId: string; to: string; subject?: string }[]
  >([]);

  const toggle = useCallback(async () => {
    if (!enabledState) {
      // Turning on — request permission if needed (matches old useTaskNotifications behavior)
      if (typeof Notification !== "undefined") {
        if (Notification.permission === "default") {
          const result = await Notification.requestPermission();
          if (result !== "granted") return;
        }
        if (Notification.permission !== "granted") return;
      }
      setEnabledState(true);
      setStoredPref(true);
    } else {
      setEnabledState(false);
      setStoredPref(false);
    }
  }, [enabledState]);

  // Flush batched notifications
  const flushNotifications = useCallback(() => {
    const changes = pendingChangesRef.current;
    pendingChangesRef.current = [];

    if (changes.length === 0) return;
    if (typeof Notification === "undefined" || Notification.permission !== "granted") return;

    if (changes.length === 1) {
      const c = changes[0];
      const title = `${projectName ? `[${projectName}] ` : ""}Task ${c.taskId} ${STATUS_LABELS[c.to] ?? c.to}`;
      new Notification(title, {
        body: c.subject ?? c.taskId,
        tag: `task-${c.taskId}`,
      });
    } else {
      const title = `${projectName ? `[${projectName}] ` : ""}${changes.length} tasks updated`;
      const body = changes
        .map((c) => `${c.taskId}: ${STATUS_LABELS[c.to] ?? c.to}`)
        .join("\n");
      new Notification(title, { body, tag: "task-batch" });
    }
  }, [projectName]);

  useEffect(() => {
    if (!statuses || !enabled) {
      if (!enabled) {
        prevStatusMap.current = null;
        initialized.current = false;
      }
      return;
    }

    const currentMap = new Map(statuses.map((t) => [t.taskId, t.status]));
    const subjectMap = new Map(
      statuses.map((t) => [t.taskId, t.subject])
    );

    if (!initialized.current || !prevStatusMap.current) {
      prevStatusMap.current = currentMap;
      initialized.current = true;
      return;
    }

    const prev = prevStatusMap.current;
    const changes: { taskId: string; to: string; subject?: string }[] = [];

    for (const [taskId, status] of currentMap) {
      const prevStatus = prev.get(taskId);
      if (prevStatus && prevStatus !== status) {
        changes.push({
          taskId,
          to: status,
          subject: subjectMap.get(taskId),
        });
      }
    }

    if (changes.length > 0) {
      pendingChangesRef.current.push(...changes);

      // Batch: wait 300ms before firing
      if (batchTimerRef.current) {
        clearTimeout(batchTimerRef.current);
      }
      batchTimerRef.current = setTimeout(flushNotifications, 300);
    }

    prevStatusMap.current = currentMap;
  }, [statuses, enabled, flushNotifications]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (batchTimerRef.current) {
        clearTimeout(batchTimerRef.current);
      }
    };
  }, []);

  return {
    enabled,
    toggle,
    supported: typeof Notification !== "undefined",
    permission:
      typeof Notification !== "undefined" ? Notification.permission : "denied",
  };
}

// ---------------------------------------------------------------------------
// useCriticalTaskNotifications — FR-038, T362
//
// A focused notification hook that fires a browser Notification only when a
// task transitions into a terminal-attention status: `needs_human` or
// `failed`. Unlike useTaskNotifications (which fires for every transition),
// this hook is intended for the always-on "alert me when something breaks"
// surface referenced by PRD FR-038: clicking the notification navigates to
// the task-detail route for the task that raised the alert.
//
// Contract:
//   - Requires browser Notification permission to emit (`granted`).
//   - Reads task statuses from the caller (typically via the
//     useRawTaskStatuses() context selector) so the hook stays pure.
//   - Persists enabled state to a dedicated localStorage key separate
//     from useTaskNotifications so the two surfaces can be toggled
//     independently.
//   - Navigates to `#/workspace/<workspaceSlug>/project/<projectId>/task/<taskId>`
//     by default; callers can override via `taskHref` for non-standard
//     routes. The click handler falls back to the bare `#/tasks/<taskId>`
//     route when no project context is supplied.
// ---------------------------------------------------------------------------

const CRITICAL_STATUSES = new Set(["needs_human", "failed"]);

function getStoredCriticalPref(): boolean {
  try {
    // Default ON — critical notifications are the primary motivation for
    // the hook. Users can opt out, but opt-in by default is intentional.
    const raw = localStorage.getItem(CRITICAL_STORAGE_KEY);
    if (raw === null) return true;
    return raw === "true";
  } catch {
    return true;
  }
}

function setStoredCriticalPref(enabled: boolean) {
  try {
    localStorage.setItem(CRITICAL_STORAGE_KEY, String(enabled));
  } catch {
    // localStorage not available — silently continue.
  }
}

interface UseCriticalTaskNotificationsOpts {
  /** Current task statuses — monitor for transitions. */
  statuses?: TaskStatus[];
  /** Explicit enable override; falls back to persisted preference. */
  enabled?: boolean;
  /** Workspace slug for the task-detail deep link (optional). */
  workspaceSlug?: string;
  /** Project id for the task-detail deep link (optional). */
  projectId?: string;
  /** Custom href builder — takes precedence over workspaceSlug/projectId. */
  taskHref?: (taskId: string) => string;
  /** Project label for the notification title. */
  projectName?: string;
}

/**
 * useCriticalTaskNotifications — browser notifications for needs_human /
 * failed transitions, with click-to-navigate deep linking into the task
 * detail route.
 */
export function useCriticalTaskNotifications(
  opts: UseCriticalTaskNotificationsOpts = {},
) {
  const {
    statuses,
    workspaceSlug,
    projectId,
    taskHref,
    projectName,
  } = opts;

  const [enabledState, setEnabledState] = useState(() => {
    if (opts.enabled !== undefined) return opts.enabled;
    return getStoredCriticalPref();
  });

  useEffect(() => {
    if (opts.enabled !== undefined) {
      setEnabledState(opts.enabled);
    }
  }, [opts.enabled]);

  const enabled = enabledState;

  const prevStatusMap = useRef<Map<string, string> | null>(null);
  const initialized = useRef(false);

  const toggle = useCallback(async () => {
    if (!enabledState) {
      if (typeof Notification !== "undefined") {
        if (Notification.permission === "default") {
          const result = await Notification.requestPermission();
          if (result !== "granted") return;
        }
        if (Notification.permission !== "granted") return;
      }
      setEnabledState(true);
      setStoredCriticalPref(true);
    } else {
      setEnabledState(false);
      setStoredCriticalPref(false);
    }
  }, [enabledState]);

  // Resolve the task-detail href for a notification click. Builder options
  // in priority order:
  //   1. Explicit `taskHref` callback — caller knows best.
  //   2. Full workspace/project route — `#/workspace/<slug>/project/<id>/task/<taskId>`.
  //   3. Fallback flat route — `#/tasks/<taskId>`.
  const resolveHref = useCallback(
    (taskId: string): string => {
      if (taskHref) return taskHref(taskId);
      if (workspaceSlug && projectId) {
        return `#/workspace/${encodeURIComponent(workspaceSlug)}/project/${encodeURIComponent(
          projectId,
        )}/task/${encodeURIComponent(taskId)}`;
      }
      return `#/tasks/${encodeURIComponent(taskId)}`;
    },
    [taskHref, workspaceSlug, projectId],
  );

  // Fire a single notification for a critical transition. Click handler
  // navigates to the task detail view and focuses the window.
  const fireNotification = useCallback(
    (taskId: string, status: string, subject?: string) => {
      if (typeof Notification === "undefined") return;
      if (Notification.permission !== "granted") return;

      const statusLabel = STATUS_LABELS[status] ?? status;
      const titlePrefix = projectName ? `[${projectName}] ` : "";
      const title = `${titlePrefix}Task ${taskId} ${statusLabel}`;
      const body = subject ?? taskId;

      const notification = new Notification(title, {
        body,
        tag: `task-critical-${taskId}`,
        requireInteraction: true,
      });

      // Route navigation on click (architecture §13 hash routing).
      notification.onclick = () => {
        try {
          if (typeof window !== "undefined") {
            window.focus();
            window.location.hash = resolveHref(taskId).replace(/^#/, "");
          }
        } finally {
          notification.close();
        }
      };
    },
    [projectName, resolveHref],
  );

  useEffect(() => {
    if (!statuses || !enabled) {
      if (!enabled) {
        prevStatusMap.current = null;
        initialized.current = false;
      }
      return;
    }

    const currentMap = new Map(statuses.map((t) => [t.taskId, t.status]));
    const subjectMap = new Map(statuses.map((t) => [t.taskId, t.subject]));

    if (!initialized.current || !prevStatusMap.current) {
      prevStatusMap.current = currentMap;
      initialized.current = true;
      return;
    }

    const prev = prevStatusMap.current;

    for (const [taskId, status] of currentMap) {
      const prevStatus = prev.get(taskId);
      // Only fire when the status changed AND the new status is critical.
      // Ignore the initial-render case (already handled by initialized
      // guard) and any non-critical transitions.
      if (
        prevStatus &&
        prevStatus !== status &&
        CRITICAL_STATUSES.has(status)
      ) {
        fireNotification(taskId, status, subjectMap.get(taskId));
      }
    }

    prevStatusMap.current = currentMap;
  }, [statuses, enabled, fireNotification]);

  return {
    enabled,
    toggle,
    supported: typeof Notification !== "undefined",
    permission:
      typeof Notification !== "undefined" ? Notification.permission : "denied",
  };
}
